/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import './EmaasEnergyUsagesBreakdownByMeterTheme.scss';
import {
  ElEMENT_PER_SCROLL,
  EnergyUsageScopeType,
  EnergyUsagesDetailExpandMeterField,
  KPI_VALUE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD,
  PreferredUnit
} from '../../../shared/Utils/Constants';
import {
  convertGasValueCheck,
  getConvertedUnit,
  getConvertedValuesViewDetails,
  getMaxBreakdownByMeter,
  getMaxValueIndexForField,
  getNullConverted,
  totalDeviationPercentageCheck
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import EnergyActualConsumptionComponent from '../../DataTableComponents/EnergyUsages/CellDataRenderer/EnergyActualConsumptionComponent';
import EnergyBreakDownChangeComponent from '../../DataTableComponents/EnergyUsages/CellDataRenderer/EnergyBreakDownChangeComponent';
import EnergyBreakDownMeterComponent from '../../DataTableComponents/EnergyUsages/CellDataRenderer/EnergyBreakDownMeterComponent';
import EnergyBreakDownBaselineComponent from '../../DataTableComponents/EnergyUsages/CellDataRenderer/EnergyBreakDownBaselineComponent';
import {
  IBreakdownByFloor,
  IUtilityMeterForArea
} from './IEnergyUsagesBreakdownProps';
import { IAny } from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  expanderTemplate,
  renderTableColumns
} from '../../../shared/MeterSavingsDetailTable/MeterSavingsDetailTableUtility';
import {
  Fivek,
  rowExpansionTemplate
} from '../../PortfolioEnergyUsageViewDetails/PremiumSubscription/AllUtilitiesDetails/BottomSection/TableColumnRenderUtils';
import { Icon } from '@scuf/common';
import { ApiContext } from '../../../../context/ApiContext';
import {
  ConversionType,
  getConvertedValue,
  getMaxConvertedValueFromArray
} from '../../../../main';
import {
  scaleData,
  scaleDataForOtherUtility
} from '../../../shared/Utils/EnergyUsagesFloorViewDetailsUtils';

const emaasDetailBreakdownByFloor = (props: IAny) => {
  const {
    columnDetails,
    energyMeasurementUnit,
    sourceType,
    meterData,
    getNextMeterValues
  } = props;
  const {
    onClickViewDetailsMeterTrend,
    enableViewDetailsMeterTrend,
    userPreferedUnit,
    getNextMetersData,
    showChangeWhenData,
    toggleEnableKwhGas
  } = useContext(ApiContext);
  const [expandedRows, setexpandedRows] = useState<any>([
    meterData[getMaxValueIndexForField(meterData, 'totalActualEmission')]
  ]);
  const [fieldSortOrder, setFieldSortOrder] = useState<number>(-1);
  const [sortFieldName, setSortFieldName] = useState<string>(
    EnergyUsagesDetailExpandMeterField.actualConsumption
  );
  const cellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    if (field === EnergyUsagesDetailExpandMeterField.actualConsumption) {
      const { totalActualEmission: convertedValue } = rowData;
      return (
        <EnergyActualConsumptionComponent
          value={value}
          unit={rowData.actualUnit}
          savings={
            rowData.rawTotalDeviation === null ? 0 : rowData.rawTotalDeviation
          }
          savingsPercentage={
            rowData.rawTotalDeviation === null
              ? 0
              : rowData.percentageEmissionsSaving
          }
          absoluteActual={rowData.rawTotalActualEmission}
          absolutePrevPeriod={
            rowData.rawTotalAbsoluteBaselineConsumption === 0
              ? null
              : rowData.rawTotalAbsoluteBaselineConsumption
          }
          progessBarRelativeWidth={rowData.progessBarRelativeWidth}
          energyMeasurementUnit={energyMeasurementUnit}
          sourceType={
            sourceType.toLowerCase() === 'all'
              ? ConversionType.ELECTRICITY
              : sourceType
          }
        />
      );
    }
    if (field === EnergyUsagesDetailExpandMeterField.change) {
      const { totalDeviation: convertedValue, deviationScaledData } = rowData;
      let changeProps;
      changeProps = {
        value: rowData.totalDeviation === null ? null : rowData.totalDeviation,
        unit: rowData.changeUnit,
        energyMeasurementUnit: energyMeasurementUnit,
        percentageEmissionsSaving: rowData.percentageEmissionsSaving,
        sourceType:
          sourceType.toLowerCase() === 'all'
            ? ConversionType.ELECTRICITY
            : sourceType
      };
      // if (
      //   convertedValue === 0 &&
      //   deviationScaledData &&
      //   deviationScaledData.Value !== 0
      // ) {
      //   changeProps = {
      //     ...changeProps,
      //     value: deviationScaledData.Value,
      //     unit: deviationScaledData.Unit,
      //     energyMeasurementUnit
      //   };
      // }
      return <EnergyBreakDownChangeComponent {...changeProps} />;
    }
    if (field === EnergyUsagesDetailExpandMeterField.meter) {
      const meterProps = {
        totalDeviation: rowData.percentageEmissionsSaving,
        utilityType: rowData.utilityType,
        isIconShow: false,
        meterCount: rowData?.utilityMeters?.length
      };
      return <EnergyBreakDownMeterComponent {...meterProps} />;
    }
    if (field === EnergyUsagesDetailExpandMeterField.baselineConsumption) {
      const { totalPreviousPeriodEmission: convertedValue } = rowData;
      const actualBaseLine =
        convertedValue === null
          ? null
          : Number(convertedValue).toFixed(KPI_VALUE_DECIMAL_PRECISION);
      const baselineProps = {
        value: value,
        unit: rowData.baselineUnit,
        energyMeasurementUnit: energyMeasurementUnit,
        sourceType:
          sourceType.toLowerCase() === 'all'
            ? ConversionType.ELECTRICITY
            : sourceType
      };
      return <EnergyBreakDownBaselineComponent {...baselineProps} />;
    }
    return <></>;
  };

  const rowcellDataRenderer = (cellData: ICellData) => {
    const { value, field, rowData } = cellData;
    if (field === EnergyUsagesDetailExpandMeterField.actualConsumption) {
      const {
        totalActualEmission: convertedValue,
        isExcludeBaseline
      } = rowData;
      return (
        <EnergyActualConsumptionComponent
          value={value}
          unit={rowData.actualUnit}
          savings={
            isExcludeBaseline || rowData.rawTotalDeviation === null
              ? 0
              : rowData.rawTotalDeviation
          }
          savingsPercentage={
            isExcludeBaseline || rowData.rawTotalDeviation === null
              ? 0
              : rowData.percentageEmissionsSaving
          }
          progessBarRelativeWidth={rowData.progessBarRelativeWidth}
          absoluteActual={rowData.rawTotalActualEmission}
          absolutePrevPeriod={
            isExcludeBaseline ||
            rowData.rawTotalAbsoluteBaselineConsumption === 0
              ? null
              : rowData.rawTotalAbsoluteBaselineConsumption
          }
          energyMeasurementUnit={energyMeasurementUnit}
          sourceType={
            sourceType.toLowerCase() === 'all'
              ? ConversionType.ELECTRICITY
              : sourceType
          }
        />
      );
    }

    if (field === EnergyUsagesDetailExpandMeterField.meter) {
      const meterProps = {
        totalDeviation: rowData.totalDeviationPercentage,
        utilityType: rowData.meterName,
        isIconShow: true
      };
      return <EnergyBreakDownMeterComponent {...meterProps} />;
    }

    if (field === EnergyUsagesDetailExpandMeterField.baselineConsumption) {
      const { totalPreviousPeriodEmission: convertedValue } = rowData;
      return value === null ? (
        <>--</>
      ) : (
        <span className="score-category-meterdata-meter floor-wise-baseline-consumption">
          {
            getConvertedValue(
              sourceType
                ? sourceType.toLowerCase() === 'all'
                  ? ConversionType?.ELECTRICITY
                  : sourceType
                : ConversionType?.ELECTRICITY,
              energyMeasurementUnit,
              Number(value),
              rowData.baselineUnit
            ).Value
          }{' '}
          {/* {getNullConverted(convertedValue)}{' '} */}
          {getConvertedUnit(value, rowData.baselineUnit)}
        </span>
      );
    }
    if (field === EnergyUsagesDetailExpandMeterField.change) {
      const { totalDeviation: convertedValue, deviationScaledData } = rowData;
      let changeProps;
      changeProps = {
        value: convertedValue,
        unit: rowData.changeUnit,
        percentageEmissionsSaving: rowData.totalDeviationPercentage,
        energyMeasurementUnit: energyMeasurementUnit,
        sourceType:
          sourceType.toLowerCase() === 'all'
            ? ConversionType.ELECTRICITY
            : sourceType
      };
      // if (
      //   convertedValue === 0 &&
      //   deviationScaledData &&
      //   deviationScaledData.Value !== 0
      // ) {
      //   changeProps = {
      //     ...changeProps,
      //     value: deviationScaledData.Value,
      //     unit: deviationScaledData.Unit
      //   };
      // }
      return <EnergyBreakDownChangeComponent {...changeProps} />;
    }
    if (field === EnergyUsagesDetailExpandMeterField.eqdetails) {
      return (
        <div
          className="trend-vis-icon-wrapper"
          onClick={(e) => {
            enableViewDetailsMeterTrend &&
              onClickViewDetailsMeterTrend &&
              onClickViewDetailsMeterTrend(rowData.meterId, rowData.meterName);
          }}
        >
          <Icon name="graph" root="global" />
        </div>
      );
    }
    return <></>;
  };

  const [dynamicMeterData, setDynamicMeterData] = useState<any>(meterData);

  const fromUnit =
    sourceType && sourceType.toLowerCase() === 'water'
      ? PreferredUnit.Water
      : userPreferedUnit === 'megajoule'
      ? userPreferedUnit
      : sourceType === ConversionType.GAS
      ? toggleEnableKwhGas
        ? PreferredUnit.Electricity
        : PreferredUnit.Gas
      : (PreferredUnit.Electricity as any);

  const processMeterData = (
    testWithSpecificMeters: IAny,
    meterWiseAggregation: IAny
  ) => {
    return meterWiseAggregation.map((item: IAny) => {
      const specificMeters = testWithSpecificMeters.filter(
        (testItem: IAny) => testItem.meterId === item.meterId
      )[0];
      if (specificMeters) {
        return specificMeters;
      }
      return item;
    });
  };

  const getNextMeterData = async (
    firstIndex: number,
    lastIndex: number,
    area: IAny,
    resolve: IAny
  ) => {
    const zoneElements =
      dynamicMeterData[
        dynamicMeterData.findIndex((x: IAny) => x.utilityType === area)
      ];
    const areaMeters = zoneElements.utilityMeters;

    const zoneId = zoneElements.id;

    const meterIds = areaMeters
      .slice(firstIndex, lastIndex)
      .map((mtr: IAny) => mtr.meterId);

    if (getNextMetersData) {
      // pass zone id in the below function instead of area name after API integration
      const metersList: IAny = await getNextMetersData(
        zoneId,
        meterIds,
        sourceType
      );
      if (metersList && metersList.status && metersList.status === 400) {
        resolve([]);
      } else if (metersList && metersList.status && metersList.status === 204) {
        resolve([]);
      } else {
        const scaledMetersList = [
          EnergyUsageScopeType.ELECTRIC,
          EnergyUsageScopeType.Gas,
          EnergyUsageScopeType.WATER
        ].includes(sourceType && sourceType.toLowerCase())
          ? scaleDataForOtherUtility(
              metersList,
              sourceType,
              fromUnit,
              energyMeasurementUnit,
              showChangeWhenData,
              zoneElements.progessBarRelativeWidth
            )
          : scaleData(
              metersList,
              userPreferedUnit ? userPreferedUnit : 'kilowatt hour',
              energyMeasurementUnit,
              showChangeWhenData,
              zoneElements.progessBarRelativeWidth
            );
        const processedData = processMeterData(scaledMetersList, areaMeters);
        const tempDynamicMeterData = JSON.parse(
          JSON.stringify(dynamicMeterData)
        );
        tempDynamicMeterData[
          tempDynamicMeterData.findIndex((x: IAny) => x.utilityType === area)
        ].utilityMeters = processedData;
        setDynamicMeterData(tempDynamicMeterData);

        resolve(processedData.slice(firstIndex, lastIndex));
      }
    }
  };

  return (
    <div className="Savings-Datatable">
      <DataTable
        className="nonflat-table Savingtable"
        expandedRows={expandedRows}
        rowExpansionTemplate={
          (data: IAny) => {
            console.log('data while rowExpansionTemplate', data);
            return (
              <Fivek
                data={data}
                NumberOfItemsPerPage={ElEMENT_PER_SCROLL}
                columnDetails={columnDetails}
                rowcellDataRenderer={rowcellDataRenderer}
                dynamicMeterData={dynamicMeterData}
                getNextData={(first: IAny, last: IAny, area: string) => {
                  return new Promise((resolve, reject) => {
                    try {
                      if (
                        dynamicMeterData[
                          dynamicMeterData.findIndex(
                            (x: IAny) => x.utilityType === area
                          )
                        ].utilityMeters[first].totalActualEmission &&
                        dynamicMeterData[
                          dynamicMeterData.findIndex(
                            (x: IAny) => x.utilityType === area
                          )
                        ].utilityMeters[last - 1].totalActualEmission
                      ) {
                        resolve(
                          dynamicMeterData[
                            dynamicMeterData.findIndex(
                              (x: IAny) => x.utilityType === area
                            )
                          ].utilityMeters.slice(first, last)
                        );
                      } else {
                        getNextMeterData(first, last, area, resolve);
                      }
                    } catch (e) {
                      resolve([]);
                    }
                  });
                }}
              />
            );
          }
          // Use the imported 'FiveK' component
        }
        data={meterData}
        onSort={({ sortField, sortOrder }) => {
          setFieldSortOrder(sortOrder as number);
          setSortFieldName(sortField);
        }}
        sortOrder={fieldSortOrder}
        sortField={sortFieldName}
      >
        <DataTable.Column
          initialWidth="1.5%"
          header=""
          field=""
          renderer={(data: ICellData) =>
            expanderTemplate(data, expandedRows, setexpandedRows)
          }
        />
        {renderTableColumns(columnDetails, cellDataRenderer)}
      </DataTable>
    </div>
  );
};
export default emaasDetailBreakdownByFloor;
